import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChampionshipsPage() {
  const [championships, setChampionships] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/championships`).then(r => {
      setChampionships(r.data);
      if (r.data.length > 0) setSelected(r.data[0]);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="championships-page" className="aged-paper">
      {/* Header */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="segment-label">The Gold</div>
            <div className="flex-1 h-[1px] bg-foreground/15" />
          </div>
          <h1 data-testid="championships-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-4">
            Championships
          </h1>
          <div className="h-[3px] w-16 gold-accent" />
        </div>
      </section>

      <div className="vintage-divider" />

      {/* Championship selector (if multiple) */}
      {championships.length > 1 && (
        <section className="py-8 px-4 bg-muted">
          <div className="max-w-7xl mx-auto flex flex-wrap gap-3">
            {championships.map(c => (
              <button
                key={c.id}
                data-testid={`champ-tab-${c.id}`}
                onClick={() => setSelected(c)}
                className={`font-heading text-sm uppercase px-6 py-3 transition-colors ${
                  selected?.id === c.id
                    ? "bg-foreground text-background"
                    : "bg-background text-foreground hover:bg-foreground hover:text-background"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Featured championship */}
      {selected && (
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Belt showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
              {/* Belt image */}
              <div className="lg:col-span-6">
                <div className="relative">
                  <div className="studio-dark p-6 sm:p-8">
                    <img
                      src={selected.image_url}
                      alt={selected.name}
                      data-testid="championship-belt-image"
                      className="w-full h-auto object-contain"
                    />
                  </div>
                  <div className="absolute top-0 left-0 bg-secondary px-4 py-2">
                    <Trophy size={16} className="text-[#E8DFC8]" />
                  </div>
                </div>
                {/* Status bar */}
                <div className="h-1 gold-accent" />
              </div>

              {/* Championship info */}
              <div className="lg:col-span-6">
                <div className="flex items-center gap-3 mb-4">
                  <Crown size={20} className="text-primary" />
                  <p className="font-body text-xs text-primary uppercase tracking-[0.3em]">Championship Title</p>
                </div>
                <h2 data-testid="championship-name" className="font-heading text-3xl sm:text-4xl uppercase mb-2 leading-tight">
                  {selected.name}
                </h2>
                <div className="h-[3px] w-16 gold-accent mb-8" />

                <div className="font-body text-base leading-relaxed space-y-5 text-foreground/85">
                  {selected.description && (
                    <p className="first-letter:text-4xl first-letter:font-heading first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:leading-none">
                      {selected.description}
                    </p>
                  )}
                </div>

                {/* Championship history */}

                {/* Current champion display (when holder is set and not vacant) */}
                {selected.current_holder && selected.current_holder !== "Vacant" && selected.current_holder !== "" && (
                  <div className="mt-10 studio-dark p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <Crown className="w-4 h-4 text-[#B09030]" />
                      <p className="font-heading text-base text-[#B09030] uppercase">Reigning Champion</p>
                    </div>
                    <p className="font-heading text-2xl text-[#E8DFC8] uppercase">{selected.current_holder}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other championships list */}
      {championships.length > 1 && (
        <>
          <div className="vintage-divider" />
          <section className="py-16 sm:py-20 px-4">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl uppercase mb-8">All Titles</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {championships.map(c => (
                  <button
                    key={c.id}
                    data-testid={`championship-card-${c.id}`}
                    onClick={() => { setSelected(c); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="text-left group"
                  >
                    <div className="bg-foreground p-4 overflow-hidden">
                      <img src={c.image_url} alt={c.name} className="w-full h-48 object-contain group-hover:scale-105 transition-transform" />
                    </div>
                    <div className="bg-muted p-4">
                      <h3 className="font-heading text-base uppercase group-hover:text-primary transition-colors">{c.name}</h3>
                      <p className="font-body text-xs text-muted-foreground uppercase tracking-wider mt-1">
                        Championship
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {championships.length === 0 && (
        <section className="py-24 px-4 text-center">
          <Trophy size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-body text-muted-foreground">Championships coming soon.</p>
        </section>
      )}
    </div>
  );
}
